import React, { useState, useEffect } from 'react';
import { apiEndpoints } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import LeasingForm from '../components/LeasingForm';
import DocViewer from '../components/DocViewer';
import { confirmDialog } from '../utils/confirm';

const getLeasingFileUrl = (filename) => {
  if (!filename) return null;
  const base = (process.env.REACT_APP_API_URL || 'http://localhost:4000').replace(/\/api\/?$/, '');
  return `${base}/uploads/leasing/${filename}`;
};

const getLeasingFiles = (l) => (l?.files?.length ? l.files : (l?.fileUrl ? [{ fileUrl: l.fileUrl, fileOrigName: l.fileOrigName }] : []));
const getLeasingPreview = (l) => {
  const list = getLeasingFiles(l);
  return { urls: list.map((f) => getLeasingFileUrl(f.fileUrl)), filenames: list.map((f) => f.fileOrigName || f.fileUrl) };
};

const STATUS_COLORS = { active: '#10b981', completed: '#6b7280', terminated: '#ef4444' };

const Leasing = () => {
  const { searchQuery, isAdmin } = useAppContext();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [selected, setSelected] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);

  const fetch = async () => {
    try {
      setLoading(true);
      const res = await apiEndpoints.leasing.list();
      setContracts(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetch(); }, []);

  const handleSave = async (data, file) => {
    if (editingContract) {
      await apiEndpoints.leasing.update(editingContract._id, data, file);
    } else {
      await apiEndpoints.leasing.create(data, file);
    }
    await fetch();
    setShowForm(false);
    setEditingContract(null);
  };

  const handleDelete = async (id) => {
    if (!await confirmDialog('Delete this leasing contract?')) return;
    await apiEndpoints.leasing.delete(id);
    setSelected(null);
    await fetch();
  };

  const handleMarkPayment = async (contractId, index, paid) => {
    await apiEndpoints.leasing.markPayment(contractId, index, { paid });
    const res = await apiEndpoints.leasing.list();
    setContracts(res.data);
    const updated = res.data.find((c) => c._id === contractId);
    if (updated) setSelected(updated);
  };

  const formatDate = (v) => v ? new Date(v).toLocaleDateString() : '—';
  const formatMoney = (v, cur = 'RON') => v ? `${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 })} ${cur}` : '—';

  if (loading) return <div className="page-loading">Loading leasing contracts...</div>;

  if (selected) {
    const paidCount = selected.schedule?.filter((p) => p.paid).length || 0;
    const totalCount = selected.schedule?.length || 0;
    const paidAmount = selected.schedule?.filter((p) => p.paid).reduce((s, p) => s + p.amount, 0) || 0;

    return (
      <div>
        <button className="back-btn" onClick={() => setSelected(null)}>
          <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" /></svg>
          Back to leasing
        </button>
        <div className="vprofile-header">
          <div className="vprofile-plate"><span className="plate-pill">{selected.plate}</span></div>
          <div className="vprofile-model">{selected.leasingCompany}</div>
          <div className="vprofile-sub">{selected.company ? `${selected.company} • ` : ''}Contract {selected.contractNumber || '—'} • {formatDate(selected.startDate)} – {formatDate(selected.endDate)}</div>
        </div>
        <div className="detail-grid">
          <div className="detail-left">
            <div className="card">
              <div className="card-header">
                <div className="card-title">Contract Details</div>
                {isAdmin && (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => { setEditingContract(selected); setShowForm(true); }}>Edit</button>
                    <button className="btn btn-danger btn-sm" onClick={() => handleDelete(selected._id)}>Delete</button>
                  </div>
                )}
              </div>
              <div className="card-body">
                {[
                  ['Vehicle', selected.plate],
                  ['Fleet Company', selected.company || '—'],
                  ['Leasing Company', selected.leasingCompany],
                  ['Contract No.', selected.contractNumber || '—'],
                  ['Start Date', formatDate(selected.startDate)],
                  ['End Date', formatDate(selected.endDate)],
                  ['Monthly Installment', formatMoney(selected.monthlyInstallment, selected.currency)],
                  ['Total Value', formatMoney(selected.totalValue, selected.currency)],
                  ['Residual Value', formatMoney(selected.residualValue, selected.currency)],
                  ['Paid so far', formatMoney(paidAmount, selected.currency)],
                  ['Payments', `${paidCount} / ${totalCount}`],
                ].map(([lbl, val]) => (
                  <div key={lbl} className="info-row">
                    <div className="info-lbl">{lbl}</div>
                    <div className="info-val">{val}</div>
                  </div>
                ))}
                <div className="info-row">
                  <div className="info-lbl">Status</div>
                  <div className="info-val">
                    <span style={{ padding: '3px 10px', borderRadius: '9999px', fontSize: '12px', fontWeight: 700, color: STATUS_COLORS[selected.status], backgroundColor: `${STATUS_COLORS[selected.status]}18` }}>{selected.status?.toUpperCase()}</span>
                  </div>
                </div>
                <div className="info-row" style={{ borderBottom: 'none' }}>
                  <div className="info-lbl">Contract Document{getLeasingFiles(selected).length > 1 ? 's' : ''}</div>
                  <div className="info-val">
                    {getLeasingFiles(selected).length > 0 ? (
                      <div style={{ display: 'grid', gap: 4, justifyItems: 'end' }}>
                        {getLeasingFiles(selected).map((f, idx) => (
                          <button
                            key={f.fileUrl || idx}
                            className="btn btn-ghost btn-xs"
                            style={{ color: '#0077b6', padding: 0 }}
                            onClick={() => setPreviewDoc({ ...getLeasingPreview(selected), initialIndex: idx })}
                          >
                            {f.fileOrigName || f.fileUrl} — Preview
                          </button>
                        ))}
                      </div>
                    ) : '—'}
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-header"><div className="card-title">Payment Schedule (Scadentar)</div></div>
              <div className="card-body" style={{ overflowX: 'auto', padding: '0' }}>
                {!selected.schedule || selected.schedule.length === 0 ? (
                  <div style={{ color: 'var(--text-3)', padding: '12px 16px' }}>No schedule generated.</div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', minWidth: '380px' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <th style={{ textAlign: 'left', padding: '10px 14px', color: '#9ca3af', fontWeight: 600, fontSize: '10px', whiteSpace: 'nowrap' }}>DUE DATE</th>
                        <th style={{ textAlign: 'left', padding: '10px 14px', color: '#9ca3af', fontWeight: 600, fontSize: '10px', whiteSpace: 'nowrap' }}>AMOUNT</th>
                        <th style={{ textAlign: 'left', padding: '10px 14px', color: '#9ca3af', fontWeight: 600, fontSize: '10px' }}>STATUS</th>
                        <th style={{ textAlign: 'left', padding: '10px 14px', color: '#9ca3af', fontWeight: 600, fontSize: '10px', whiteSpace: 'nowrap' }}>PAID DATE</th>
                        <th style={{ width: '80px' }} />
                      </tr>
                    </thead>
                    <tbody>
                      {selected.schedule.map((p, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '8px 14px', whiteSpace: 'nowrap' }}>{formatDate(p.dueDate)}</td>
                          <td style={{ padding: '8px 14px', fontWeight: 600, whiteSpace: 'nowrap' }}>{formatMoney(p.amount, selected.currency)}</td>
                          <td style={{ padding: '8px 14px' }}>
                            <span style={{ padding: '2px 7px', borderRadius: '9999px', fontSize: '10px', fontWeight: 700, color: p.paid ? '#10b981' : '#f59e0b', backgroundColor: p.paid ? '#ecfdf5' : '#fffbeb' }}>
                              {p.paid ? 'PAID' : 'PENDING'}
                            </span>
                          </td>
                          <td style={{ padding: '8px 14px', color: 'var(--text-3)', fontSize: '11px', whiteSpace: 'nowrap' }}>{p.paidDate ? formatDate(p.paidDate) : '—'}</td>
                          <td style={{ padding: '8px 14px', textAlign: 'right' }}>
                            {isAdmin && <button className="btn btn-ghost btn-xs" style={{ fontSize: '11px', whiteSpace: 'nowrap' }} onClick={() => handleMarkPayment(selected._id, i, !p.paid)}>
                              {p.paid ? 'Unpaid' : 'Mark Paid'}
                            </button>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>

          <div className="detail-right">
            <div className="card">
              <div className="card-header"><div className="card-title">Progress</div></div>
              <div className="card-body" style={{ textAlign: 'center', padding: '20px' }}>
                <div style={{ fontSize: '36px', fontWeight: 800, color: '#0077b6' }}>{totalCount > 0 ? Math.round((paidCount / totalCount) * 100) : 0}%</div>
                <div style={{ fontSize: '13px', color: 'var(--text-3)', marginBottom: '16px' }}>{paidCount} of {totalCount} payments made</div>
                <div style={{ background: '#f3f4f6', borderRadius: '999px', height: '8px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${totalCount > 0 ? (paidCount / totalCount) * 100 : 0}%`, background: '#10b981', borderRadius: '999px' }} />
                </div>
                <div style={{ marginTop: '20px', fontSize: '20px', fontWeight: 700, color: 'var(--text-1)' }}>{formatMoney(paidAmount, selected.currency)}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-3)' }}>paid so far</div>
                <div style={{ marginTop: '12px', fontSize: '20px', fontWeight: 700, color: '#ef4444' }}>{formatMoney((totalCount - paidCount) * selected.monthlyInstallment, selected.currency)}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-3)' }}>remaining</div>
              </div>
            </div>

            {getLeasingFiles(selected).length > 0 && (
              <div className="card">
                <div className="card-header"><div className="card-title">Contract Document{getLeasingFiles(selected).length > 1 ? 's' : ''}</div></div>
                <div className="card-body">
                  <button
                    className="btn btn-primary"
                    style={{ width: '100%' }}
                    onClick={() => setPreviewDoc(getLeasingPreview(selected))}
                  >
                    Preview Contract{getLeasingFiles(selected).length > 1 ? `s (${getLeasingFiles(selected).length})` : ''}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        <LeasingForm isOpen={showForm} onClose={() => { setShowForm(false); setEditingContract(null); }} leasing={editingContract} onSave={handleSave} />
        <DocViewer urls={previewDoc?.urls} filenames={previewDoc?.filenames} initialIndex={previewDoc?.initialIndex || 0} onClose={() => setPreviewDoc(null)} />
      </div>
    );
  }

  const visibleContracts = searchQuery
    ? contracts.filter((c) => {
        const q = searchQuery.toLowerCase();
        return [c.plate, c.company, c.leasingCompany, c.contractNumber, c.status].some((v) => v?.toLowerCase().includes(q));
      })
    : contracts;

  return (
    <div>
      <div className="ph">
        <div>
          <div className="ph-title">Leasing</div>
          <div className="ph-sub">{contracts.filter((c) => c.status === 'active').length} active contract{contracts.filter((c) => c.status === 'active').length !== 1 ? 's' : ''}</div>
        </div>
        {isAdmin && <button className="btn btn-primary btn-sm" onClick={() => { setEditingContract(null); setShowForm(true); }}>+ Add Contract</button>}
      </div>

      <div className="card">
        <div className="card-body" style={{ overflowX: 'auto', padding: 0 }}>
          {visibleContracts.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-3)' }}>No leasing contracts yet.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', tableLayout: 'auto' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  {['PLATE', 'FLEET COMPANY', 'LEASING CO.', 'CONTRACT NO.', 'START', 'END', 'MONTHLY', 'PAYMENTS', 'STATUS', ''].map((h) => (
                    <th key={h} style={{ textAlign: 'left', padding: '12px', color: '#9ca3af', fontWeight: 600, fontSize: '11px', letterSpacing: '0.05em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleContracts.map((c) => {
                  const paid = c.schedule?.filter((p) => p.paid).length || 0;
                  const total = c.schedule?.length || 0;
                  return (
                    <tr key={c._id} style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }} onClick={() => setSelected(c)}>
                      <td style={{ padding: '12px' }}><span className="plate">{c.plate}</span></td>
                      <td style={{ padding: '12px', color: 'var(--text-3)', fontSize: '12px' }}>{c.company || '—'}</td>
                      <td style={{ padding: '12px', fontWeight: 600 }}>{c.leasingCompany}</td>
                      <td style={{ padding: '12px', color: 'var(--text-3)' }}>{c.contractNumber || '—'}</td>
                      <td style={{ padding: '12px' }}>{formatDate(c.startDate)}</td>
                      <td style={{ padding: '12px' }}>{formatDate(c.endDate)}</td>
                      <td style={{ padding: '12px', fontWeight: 600 }}>{c.monthlyInstallment?.toLocaleString()} {c.currency}</td>
                      <td style={{ padding: '12px' }}>{paid}/{total}</td>
                      <td style={{ padding: '12px' }}>
                        <span style={{ padding: '3px 8px', borderRadius: '9999px', fontSize: '11px', fontWeight: 700, color: STATUS_COLORS[c.status], backgroundColor: `${STATUS_COLORS[c.status]}18` }}>
                          {c.status?.toUpperCase()}
                        </span>
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>
                        {getLeasingFiles(c).length > 0 ? (
                          <button className="btn btn-ghost btn-xs" style={{ color: '#10b981', marginRight: 6 }} onClick={(e) => { e.stopPropagation(); setPreviewDoc(getLeasingPreview(c)); }}>Preview</button>
                        ) : (
                          <span className="btn btn-ghost btn-xs" style={{ color: '#d1d5db', cursor: 'default', marginRight: 6 }}>Preview</span>
                        )}
                        <button className="btn btn-ghost btn-xs" onClick={(e) => { e.stopPropagation(); setSelected(c); }}>View →</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <LeasingForm isOpen={showForm} onClose={() => { setShowForm(false); setEditingContract(null); }} leasing={editingContract} onSave={handleSave} />
      <DocViewer urls={previewDoc?.urls} filenames={previewDoc?.filenames} initialIndex={previewDoc?.initialIndex || 0} onClose={() => setPreviewDoc(null)} />
    </div>
  );
};

export default Leasing;
